import type { Preferences, PreferencesResponse, PreferencesUpdate, ProfileResponse } from "@lms/shared-types";
import { useId, useState, type ReactNode } from "react";
import { fetchJson } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, PageHeader, TextField } from "../../components/ui";
import { useAnnounce } from "../../lib/announce";
import { usePreferences } from "../../lib/format";
import { detectedTimeZone } from "../../lib/preferences";
import { SecuritySections } from "./SecurityPage";

/**
 * One Account page, not seven settings routes (profile template pitfall
 * 9): Profile, Preferences, Security, Access. Every preference saves as it
 * changes — one field per request (PATCH leaves the rest alone) — and the
 * appearance follows at once, so a person can see and hear the effect of
 * a change before moving on. "On this page" is the in-page navigation the
 * screen-reader walk expects of a long page.
 */
export const ROLE_LABEL: Record<string, string> = {
  learner: "Learner",
  applicant: "Applicant",
  apprentice: "Apprentice",
  mentor: "Mentor",
  instructor: "Instructor",
  "program-director": "Program director",
  "compliance-admin": "Compliance admin",
  "tenant-admin": "Organization admin",
  employer: "Employer",
  auditor: "Auditor",
  "platform-operator": "Platform operator",
};
const SCOPE_LABEL: Record<string, string> = { tenant: "for the whole organization", program: "for the program", employer_participation: "for the employer's participation", platform: "across the platform" };

const timeZones = (): string[] => {
  try {
    return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? ["UTC"];
  } catch {
    return ["UTC"];
  }
};

function Select<K extends keyof Preferences>({ label, hint, name, value, options, onChange }: { label: string; hint?: string; name: K; value: string; options: Array<{ value: string; label: string }>; onChange: (name: K, value: string) => void }) {
  const id = useId();
  return (
    <div className="grid gap-1">
      <label htmlFor={id} className="font-medium">
        {label}
      </label>
      {hint && (
        <p id={`${id}-hint`} className="text-sm text-muted">
          {hint}
        </p>
      )}
      <select id={id} aria-describedby={hint ? `${id}-hint` : undefined} className="min-h-11 w-full min-w-0 max-w-full rounded-lg border border-line bg-surface px-3 text-base md:text-sm" value={value} onChange={(ev) => onChange(name, ev.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Toggle<K extends keyof Preferences>({ label, hint, name, checked, onChange }: { label: string; hint?: string; name: K; checked: boolean; onChange: (name: K, value: boolean) => void }) {
  const id = useId();
  return (
    <div className="flex items-start gap-3">
      <input id={id} type="checkbox" className="mt-1 size-5" checked={checked} aria-describedby={hint ? `${id}-hint` : undefined} onChange={(ev) => onChange(name, ev.target.checked)} />
      <div>
        <label htmlFor={id} className="font-medium">
          {label}
        </label>
        {hint && (
          <p id={`${id}-hint`} className="text-sm text-muted">
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}

/** A Card is already a labelled section with an h2; the id is what "On this page" links to. */
function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <Card id={id} title={title} className="scroll-mt-4">
      {children}
    </Card>
  );
}

export function AccountPage() {
  const { session, config, me, refreshMe } = useAuth();
  const { preferences, update } = usePreferences();
  const announce = useAnnounce();
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [profileStatus, setProfileStatus] = useState<{ busy: boolean; error?: unknown }>({ busy: false });
  const [prefError, setPrefError] = useState<unknown>();
  if (!session || !me) return null;
  const canSeeProgram = me.permissions.includes("user.list") || me.permissions.includes("audit_event.list");

  const savePreference = async <K extends keyof Preferences>(name: K, value: Preferences[K]) => {
    const previous = preferences[name];
    update({ [name]: value } as Partial<Preferences>); // paints at once
    setPrefError(undefined);
    try {
      await fetchJson<PreferencesResponse>(config, session, "/v1/me/preferences", { method: "PATCH", body: JSON.stringify({ [name]: value } satisfies PreferencesUpdate), headers: { "content-type": "application/json" } });
      announce("Saved.");
    } catch (error) {
      update({ [name]: previous } as Partial<Preferences>);
      setPrefError(error);
      announce("That change could not be saved.");
    }
  };

  const saveProfile = async () => {
    const name = (displayName ?? me.person.displayName).trim();
    if (!name) return;
    setProfileStatus({ busy: true });
    try {
      await fetchJson<ProfileResponse>(config, session, "/v1/me/profile", { method: "PATCH", body: JSON.stringify({ displayName: name }), headers: { "content-type": "application/json" } });
      await refreshMe();
      setDisplayName(null);
      setProfileStatus({ busy: false });
      announce("Your display name is saved.");
    } catch (error) {
      setProfileStatus({ busy: false, error });
    }
  };

  return (
    <>
      <PageHeader title="Account" subtitle={`${me.person.email} at ${me.tenant.displayName}.`} />
      <nav aria-label="On this page" className="mb-6">
        <ul className="flex flex-wrap gap-x-4 gap-y-1">
          {[
            ["profile", "Profile"],
            ["preferences", "Preferences"],
            ["security", "Security"],
            ["access", "Access"],
          ].map(([id, label]) => (
            <li key={id}>
              <a href={`#${id}`}>{label}</a>
            </li>
          ))}
        </ul>
      </nav>
      <div className="space-y-6">
        <Section id="profile" title="Profile">
          <form
            className="grid max-w-xl gap-4"
            onSubmit={(ev) => {
              ev.preventDefault();
              void saveProfile();
            }}
          >
            <TextField label="Display name" description="How your name appears to others. Your email stays the name of record on every action you take." value={displayName ?? me.person.displayName} onChange={(ev) => setDisplayName(ev.target.value)} maxLength={80} error={profileStatus.error ? "The name could not be saved. Try again." : undefined} />
            <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[auto_1fr] [&>*]:min-w-0">
              <dt className="text-muted">Email</dt>
              <dd>{me.person.email}</dd>
              <dt className="text-muted">Legal name</dt>
              <dd>
                {me.person.givenName} {me.person.familyName} <span className="text-muted">— on the record; ask your program director to correct it</span>
              </dd>
              {me.identityProvider && (
                <>
                  <dt className="text-muted">Signs in through</dt>
                  <dd>{me.identityProvider}</dd>
                </>
              )}
            </dl>
            <div>
              <Button type="submit" variant="primary" disabled={profileStatus.busy || displayName === null || displayName.trim() === me.person.displayName}>
                {profileStatus.busy ? "Saving…" : "Save display name"}
              </Button>
            </div>
          </form>
        </Section>

        <Section id="preferences" title="Preferences">
          <p className="text-muted">Each change saves as you make it and applies straight away, here and on every device you sign in from.</p>
          {prefError !== undefined && (
            <p className="mt-2 text-danger" role="alert">
              That change could not be saved; it has been put back.
            </p>
          )}
          <div className="mt-4 grid min-w-0 gap-6 lg:grid-cols-2 [&>*]:min-w-0">
            <fieldset className="grid min-w-0 gap-4">
              <legend className="mb-2 font-semibold">Appearance and reading</legend>
              <Select label="Theme" name="theme" value={preferences.theme} options={[{ value: "system", label: "Follow the device" }, { value: "light", label: "Light" }, { value: "dark", label: "Dark" }]} onChange={(n, v) => void savePreference(n, v as Preferences["theme"])} />
              <Select label="Contrast" name="contrast" value={preferences.contrast} options={[{ value: "auto", label: "Follow the device" }, { value: "default", label: "Default" }, { value: "more", label: "More contrast" }]} onChange={(n, v) => void savePreference(n, v as Preferences["contrast"])} />
              <Select label="Text size" hint="Everything scales together; the browser's zoom still works on top." name="textSize" value={preferences.textSize} options={[{ value: "default", label: "Default" }, { value: "large", label: "Large" }, { value: "x-large", label: "Extra large" }]} onChange={(n, v) => void savePreference(n, v as Preferences["textSize"])} />
              <Select label="Line spacing" name="lineSpacing" value={preferences.lineSpacing} options={[{ value: "default", label: "Default" }, { value: "relaxed", label: "Relaxed" }]} onChange={(n, v) => void savePreference(n, v as Preferences["lineSpacing"])} />
              <Select label="Motion" hint="Reduced stops animations and transitions that are not essential." name="motion" value={preferences.motion} options={[{ value: "auto", label: "Follow the device" }, { value: "standard", label: "Standard" }, { value: "reduced", label: "Reduced" }]} onChange={(n, v) => void savePreference(n, v as Preferences["motion"])} />
            </fieldset>
            <fieldset className="grid min-w-0 gap-4">
              <legend className="mb-2 font-semibold">Time, media and reminders</legend>
              <Select label="Time zone" name="timeZone" value={preferences.timeZone} options={[{ value: "auto", label: `Follow the device (${detectedTimeZone()})` }, ...timeZones().map((z) => ({ value: z, label: z }))]} onChange={(n, v) => void savePreference(n, v)} />
              <Select label="Date format" name="dateFormat" value={preferences.dateFormat} options={[{ value: "auto", label: "Follow the device" }, { value: "iso", label: "2026-09-19" }, { value: "us", label: "09/19/2026" }, { value: "eu", label: "19/09/2026" }]} onChange={(n, v) => void savePreference(n, v as Preferences["dateFormat"])} />
              <Select label="Start page" hint="Where you land after signing in." name="startPage" value={canSeeProgram || preferences.startPage === "home" ? preferences.startPage : "home"} options={[{ value: "home", label: "Home" }, ...(canSeeProgram ? [{ value: "program", label: "Program" }] : [])]} onChange={(n, v) => void savePreference(n, v as Preferences["startPage"])} />
              <Toggle label="Captions on by default" name="captions" checked={preferences.captions} onChange={(n, v) => void savePreference(n, v)} />
              <Toggle label="Show the transcript first" hint="Where a video has a transcript, open it before the player." name="transcriptFirst" checked={preferences.transcriptFirst} onChange={(n, v) => void savePreference(n, v)} />
              <Toggle label="Play media automatically" hint="Off means nothing starts until you press play." name="autoplay" checked={preferences.autoplay} onChange={(n, v) => void savePreference(n, v)} />
              <Select label="Reminders" name="reminderChannel" value={preferences.reminderChannel} options={[{ value: "email", label: "By email and in the product" }, { value: "in-app", label: "In the product only" }]} onChange={(n, v) => void savePreference(n, v as Preferences["reminderChannel"])} />
            </fieldset>
          </div>
        </Section>

        <section id="security" aria-labelledby="security-heading" className="scroll-mt-4">
          <h2 id="security-heading" className="mb-3 text-xl font-semibold">
            Security
          </h2>
          <SecuritySections />
        </section>

        <Section id="access" title="Access">
          <p className="text-muted">What you can do here comes from the roles you hold; the server decides, not this page.</p>
          <ul className="mt-3 grid gap-2" data-testid="my-roles">
            {me.roles.map((role) => {
              const grant = me.grants.find((g) => g.role === role);
              return (
                <li key={role} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-semibold">{ROLE_LABEL[role] ?? role}</span>
                  <span className="text-muted">{role === "learner" ? "everyone in the organization" : grant ? `${SCOPE_LABEL[grant.scopeKind]}${grant.scopeName ? ` ${grant.scopeName}` : ""}` : ""}</span>
                  {grant?.expiresAt && <span className="text-muted">until {grant.expiresAt.slice(0, 10)}</span>}
                </li>
              );
            })}
          </ul>
          <p className="mt-3 text-sm text-muted">{me.permissions.length} things you can do here come from these roles. What you cannot do is not shown to you as a control.</p>
        </Section>
      </div>
    </>
  );
}
